// The visitor's trips, kept current across tabs.
//
// Two tabs on the planner is not an exotic case — it is what happens when
// someone opens their own trip sheet in a second tab to look at it. Without the
// subscription this list goes stale, and worse: a tab holding a deleted trip in
// memory writes it back on the next keystroke, so the delete appears to undo
// itself. The `storage` event only fires in OTHER tabs, which is precisely the
// case this tab cannot otherwise see.
import { useCallback, useEffect, useState } from 'react';
import { listTrips, subscribeToTripStorage } from '../utils/tripStorage';

/**
 * @param {string|null} currentTripId re-read the list whenever this changes.
 *
 * Not optional, and found by driving rather than by reading: the `storage`
 * event does NOT fire in the tab that caused the write, so a visitor who
 * pressed "Start another trip" here had a switcher that still believed there
 * was one trip — it said "Trip" instead of "2 trips" and offered nothing to
 * switch to until a reload. The subscription covers other tabs; this covers
 * the one you are in.
 */
const useTripList = (currentTripId = null) => {
  const [trips, setTrips] = useState(() => listTrips());

  const refresh = useCallback(() => setTrips(listTrips()), []);

  useEffect(() => {
    refresh();
    return subscribeToTripStorage(refresh);
  }, [refresh, currentTripId]);

  return { trips, refresh };
};

export default useTripList;
